import ConnectableObject from "./ConnectableObject";
import { copyAllProperties, productionAssert } from "./utils";
import Connection, { ConnectionType } from "./Connection";
import { ChannelType, areChannelTypesCompatible, channelTypeInfo, isSameLightRolloHeating, electricalChannelType } from "./ChannelType";
import type Device from "./Device";
import type Consumer from "./Consumer";
import ChannelTreeViewModel from "../modelTreeViews/ChannelTreeviewModel";
import BaseModelMap from "./BaseModelMap";
import Scene from "./Scene";
import { KnxSceneInfo, KnxSceneInfos } from "./KnxSceneInfo";
import KnxInstanceData, { ValueLevels } from "./KnxInstanceData";
import BaseModel from "./BaseModel";
import BaseObject from "./BaseObject";
import Signal from "./Signal";
import SignalType from "./SignalType";
import { PortInfo } from "./PortInfos";
import RawSceneDataItem from "./RawSceneDataItem";
import type Page from "./Page";
import type Fuse from "./Fuse";
import type Terminal from "./Terminal";
import type Button from "./Button";
import { nameToShortName } from "../models/utils";
import { LINK_CHAR, TAG_ENVIRONMENT, TAG_MANUAL_SIGNALS, TAG_SPECIAL, TAG_TEMPERATUR } from "./Constants";

import Logger from "../Logger";
const logger = new Logger();

const _FILENAME_ = "Channel.ts"; // no itx

import { ButtonSide } from "./ButtonRockerConfig";
export { ButtonSide } from "./ButtonRockerConfig";

export enum SinglePortSide {
  left,
  right,
  top,
  bottom,
  auto,
  leftRightAuto,
}

export class ChannelDescriptionProperty {
  id = "";
  instanceId = "";
  moduleId = "";
  repeatindex?: number;
  get xid(): string {
    return `${this.id}-${this.instanceId}`;
  }
  set xid(newValue: string) {}
}

export class ChannelTypeProperty extends ChannelDescriptionProperty {
  //
  // non persistent !!!
  //
  // Is used ONLY in KnxParameterViewer
  // and should be set after a run on KnxParemeterViewer
  // during channelEvaluation
  //

  knxChannel: Channel | undefined;
  ////////////////////////////////////////////////////

  mapChannelTypeToValue = new Map<ChannelType, { optionValue: string; optionDescription: string }>();

  mapValueToChannelType(value: string): ChannelType | undefined {
    for (const [key, val] of this.mapChannelTypeToValue.entries()) {
      if (val.optionValue == value) {
        return key;
      }
    }
    return undefined;
  }

  toJSON(): any {
    return {
      id: this.id,
      instanceId: this.instanceId,
      moduleId: this.moduleId,
      repeatindex: this.repeatindex,
      mapChannelTypeToValue: Array.from(this.mapChannelTypeToValue.entries()),
    };
  }

  fromJSON(data: any): void {
    this.id = data.id;
    this.instanceId = data.instanceId;
    this.moduleId = data.moduleId;
    this.repeatindex = data.repeatindex;
    this.mapChannelTypeToValue = new Map(data.mapChannelTypeToValue);
  }
}

//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import { InstallChecks } from "./InstallationChecks";
import AllowedConnections from "./AllowedConnections";
import { EP } from "../performance/PerfomanceItem";
import Multiplexer from "./Multiplexer";
import { TypeRestriction } from "../Types";
import SignalTypes from "./SignalTypes";
import type Room from "./Room";
import { matchN } from "../KnxProductDataTools/determineActorComObjectFunction";
import { RockerConfig, SceneDefinitions } from "./KnxRules";
import { RuleIds } from "./KnxRule";
import KnxProductData from "../KnxProductdata";
import { isUiModeDeveloper, isUiModeExpert, isUiModeProductAdmin } from "../uiMode";
import type Floor from "./Floor";
import { itx } from "../translationItx/itx";
import GroupAddress from "./GroupAdress";
import { isDevelopmentEnvironment } from "../estada-config";
import DebugOutput from "../DebugOutput";

ObjectFactory.registerClass("Channel", (data?: any, parent?: BaseModel) => {
  return new Channel(data, parent);
});
//////////////////////////////////////////////////

// Generic products have no KNX parameter metadata describing their supported types.
const allowedChannelTypesForRocker = [
  ChannelType.Switch,
  ChannelType.Dimmer,
  ChannelType.TuneableWhite,
  ChannelType.Rgb,
].map((channelType) => ({ channelType }));
const allowedChannelTypesForSingle = [...allowedChannelTypesForRocker, { channelType: ChannelType.Scene }];
const allowedChannelTypesForSensor = [{ channelType: ChannelType.Switch }];

export default class Channel extends ConnectableObject {
  static functionNameForNotConnected = ""; //  MWE 18.6.2026 changed to "" to reduce reveal because of channelName changes "(nicht verbunden)";

  public get className(): string {
    return "Channel"; // no itx
  }

  getParentCollectionName(): string | undefined {
    return this.isRequestChannel ? "channels" : "knxChannels"; // no itx
  }

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    copyAllProperties(data, this);
    this.treeViewModel = new ChannelTreeViewModel(this);

    if (data && data.isRequestChannel == undefined) {
      logger.devError("isRequestChannel not defined in constructor");
    }
  }

  //////////////////////////////
  // persistent properties

  singlePortSideN = SinglePortSide.right;
  signals = new BaseModelMap<Signal>(Signal, this, "s");

  // -----------------------------------------------------------------------------
  // mappedId
  // -----------------------------------------------------------------------------
  private _mappedId: string | undefined;
  get mappedId(): string | undefined {
    return this._mappedId;
  }

  set mappedId(newValue: string | undefined) {
    if (this._mappedId === newValue) return;
    this.beforeChange("mappedId");
    this._mappedId = newValue;
    this.changed("mappedId");
  }

  // -----------------------------------------------------------------------------
  // knxChannelId
  // -----------------------------------------------------------------------------
  _knxChannelId: string | undefined;
  get knxChannelId(): string | undefined {
    return this._knxChannelId;
  }

  set knxChannelId(newValue: string | undefined) {
    if (this._knxChannelId === newValue) return;
    this.beforeChange("knxChannelId");
    this._knxChannelId = newValue;
    this.changed("knxChannelId");
  }

  // -----------------------------------------------------------------------------
  // index
  // -----------------------------------------------------------------------------
  _index = 0;
  get index(): number {
    return this._index;
  }

  set index(newValue: number) {
    if (this._index === newValue) return;
    this.beforeChange("index");
    this._index = newValue;
    this.changed("index");
  }

  // -----------------------------------------------------------------------------
  // isRequestChannel
  // -----------------------------------------------------------------------------
  _isRequestChannel = true;
  get isRequestChannel(): boolean {
    return this._isRequestChannel;
  }

  set isRequestChannel(newValue: boolean) {
    if (this._isRequestChannel === newValue) return;
    this.beforeChange("isRequestChannel");
    this._isRequestChannel = newValue;
    this.changed("isRequestChannel");
  }

  // -----------------------------------------------------------------------------
  isCreatedFromScript?: boolean;

  get isDali(): boolean {
    return (this.parent as ConnectableObject).isDali;
  }

  // -----------------------------------------------------------------------------
  // manualName
  // -----------------------------------------------------------------------------
  _manualName?: boolean;
  get manualName(): boolean | undefined {
    return this._manualName;
  }

  set manualName(newValue: boolean | undefined) {
    if (this._manualName === newValue) return;
    this.beforeChange("manualName");
    this._manualName = newValue;
    this.changed("manualName");
  }

  // -----------------------------------------------------------------------------
  // autoConfig
  // -----------------------------------------------------------------------------
  _autoConfig?: boolean;
  get autoConfig(): boolean | undefined {
    return this._autoConfig;
  }

  set autoConfig(newValue: boolean | undefined) {
    if (this._autoConfig === newValue) return;
    this.beforeChange("autoConfig");
    this._autoConfig = newValue;
    this.changed("autoConfig");
  }

  // -----------------------------------------------------------------------------

  isNewCreatedFromAutoconnect = false;

  _installChecks: InstallChecks | undefined = undefined;

  // a temporary flag for Device.evalAllFromKnxMetadata
  existsBeforeThisEvaluation?: boolean = false;
  /////////////////////////////////////////////////////

  get installChecks(): InstallChecks {
    if (!this._installChecks) {
      this._installChecks = new InstallChecks(this.id);
    }
    return this._installChecks;
  }

  removeMapping() {
    if (this.mappedChannel) {
      this.mappedChannel.mappedId = undefined;
      this.mappedId = undefined;
    }
  }

  public get knxChannel(): Channel | undefined {
    const res = this.mappedChannel;
    res?.assetIsKnxChannel();
    return res;
  }

  public set knxChannel(newValue: Channel | undefined) {
    this.assetIsRequestChannel();
    this.mappedChannel = newValue;
  }

  public get requestChannel(): Channel | undefined {
    const res = this.mappedChannel;
    res?.assetIsRequestChannel();
    return res;
  }

  public set requestChannel(newValue: Channel | undefined) {
    this.assetIsKnxChannel();
    this.mappedChannel = newValue;
  }

  public get mappedChannel(): Channel | undefined {
    const res = this.state.objectRegistry.getObject<Channel>(this.mappedId);
    if (isDevelopmentEnvironment()) {
      if (res && res.tag != this.tag) {
        const isRequestChannel1 = res.isRequestChannel ? "request" : "knx";
        const isRequestChannel2 = this.isRequestChannel ? "request" : "knx";

        const device = this.parent?.parent;
        logger.error(
          `RequestChannel tag mismatch (mapped: ${isRequestChannel1}=${res.tag}, this: ${isRequestChannel2}=${this.tag}) device=${device?.tag} ${device?.name}`,
        );
      }
    }

    return res;
  }

  public set mappedChannel(newValue: Channel | undefined) {
    if (newValue == undefined) {
      this.removeMapping();
      return;
    } else {
      if (this.mappedId && this.mappedId != newValue.id) {
        logger.error("Remap channel", this, newValue);
        this.removeMapping();
      }

      newValue.mappedId = this.id;
      this.mappedId = newValue?.id;

      if (isDevelopmentEnvironment()) {
        if (newValue && newValue.tag != this.tag) {
          logger.error("RequestChannel tag mismatch (B)", this, newValue);
        }
      }
    }
  }

  set nameOrConnectedNames(newValue: string) {
    this.name = newValue;
  }

  get nameOrConnectedNames(): string {
    if (this.channelType == ChannelType.Environment) {
      return "";
    }

    if (this.name && this.name != Channel.functionNameForNotConnected) {
      return this.name;
    }

    let connectedNames = new Array<string>();

    this.connections.forEach((connection) => {
      if (!connection.isDeleted && !connection.isDeleting) {
        const partner = connection.getPartner(this);
        if (partner) {
          connectedNames.push(partner.name || partner.tag);
        }
      }
    });

    if (connectedNames.length == 0) {
      return Channel.functionNameForNotConnected;
    } else {
      connectedNames = connectedNames.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      return connectedNames.join(" / ");
    }
  }

  setName(newValue: string) {
    this._name = newValue;

    // if (!this.manualName) {
    //   this.setNameFromConnections();
    // }
  }

  getChannelType(): ChannelType {
    if (this.autoConfig && this.connections.size == 0) {
      return ChannelType.Generic;
    }
    return this._channelType;
  }

  getPersistentProperties(): string[] {
    return [
      "channelType", // no itx
      "signals", // no itx
      "knxChannelId", // no itx
      "mappedId", // no itx
      "index", // no itx
      "isRequestChannel", // no itx
      "knxSceneInfos", // no itx
      "knxChannelDescriptionProperty", // no itx
      "knxChannelTypeProperty", // on itx
      "instanceId", // no itx
      "wiringCheck", // no itx
      "functionCheck", // no itx
      "manualName", // no itx
      "autoConfig", // no itx
      ...super.getPersistentProperties(),
    ];
  }
  //////////////////////////////

  get extendedTag(): string {
    if (this.knxChannel) {
      return `${this.parent?.tag}.${this.knxChannel.name}`;
    } else {
      return `${this.parent?.tag}.${this.tag}`;
    }
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    if (this.className == "Channel") {
      return (this.parent as Device)?.channels.sortBy("tag");
    } else if (this.className == "ButtonChannel") {
      return (this.parent as Button)?.buttonChannels.sortBy("tag");
    }
  }

  active = true;
  selectedForAutoconect = false;

  // only for KnxPropertyView, during productDataAnalysis
  subChannelIndent = 0;

  // only for KnxPropertyView. collection properties relating to scenes
  get rawSceneData(): Array<RawSceneDataItem> {
    let res = new Array<RawSceneDataItem>();
    for (const signal of this.signals) {
      if (signal.rawSceneData && signal.rawSceneData.length > 0) {
        res = res.concat(signal.rawSceneData);
      }
    }
    return res;
  }

  public get isChannel(): boolean {
    return true;
  }

  // -----------------------------------------------------------------------------
  // knxSceneInfos
  // -----------------------------------------------------------------------------
  _knxSceneInfos = new KnxSceneInfos();
  get knxSceneInfos(): KnxSceneInfos {
    return this._knxSceneInfos;
  }

  set knxSceneInfos(newValue: KnxSceneInfos) {
    if (this._knxSceneInfos === newValue) return;
    this.beforeChange("knxSceneInfos");
    this._knxSceneInfos = newValue;
    this.changed("knxSceneInfos");
  }

  //////////////////////////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // knxChannelDescriptionProperty
  // -----------------------------------------------------------------------------
  private _knxChannelDescriptionProperty?: ChannelDescriptionProperty | undefined;
  public get knxChannelDescriptionProperty(): ChannelDescriptionProperty | undefined {
    return this._knxChannelDescriptionProperty;
  }
  public set knxChannelDescriptionProperty(value: ChannelDescriptionProperty | undefined) {
    this.beforeChange("knxChannelDescriptionProperty");
    if (!value?.id) {
      value = undefined;
    }
    this._knxChannelDescriptionProperty = value;

    this.changed("knxChannelDescriptionProperty");
  }
  //////////////////////////////////////////////////////////////////////////////////////////

  // -----------------------------------------------------------------------------
  // knxChannelTypeProperty
  // -----------------------------------------------------------------------------
  _knxChannelTypeProperty?: ChannelTypeProperty;
  get knxChannelTypeProperty(): ChannelTypeProperty | undefined {
    return this._knxChannelTypeProperty;
  }

  set knxChannelTypeProperty(newValue: ChannelTypeProperty | undefined) {
    if (this._knxChannelTypeProperty === newValue) return;
    this.beforeChange("knxChannelTypeProperty");
    this._knxChannelTypeProperty = newValue;
    this.changed("knxChannelTypeProperty");
  }

  // -----------------------------------------------------------------------------
  // instanceId
  // -----------------------------------------------------------------------------
  _instanceId?: string;
  get instanceId(): string | undefined {
    return this._instanceId;
  }

  set instanceId(newValue: string | undefined) {
    if (this._instanceId === newValue) return;
    this.beforeChange("instanceId");
    this._instanceId = newValue;
    this.changed("instanceId");
  }

  // -----------------------------------------------------------------------------

  // only for MetadataProcessing
  subChannels: Array<Channel> | undefined;

  canBeDeletedInteractive(): boolean {
    if (this.mappedChannel && !isUiModeDeveloper()) {
      return false;
    }
    if (this.tag.includes(TAG_TEMPERATUR)) {
      return false;
    }
    return true;
  }

  canBeEditedInteractive(): boolean {
    return true;
  }

  canBeCopiedInteractive(): boolean {
    return false;
  }

  deleteOnlyThisChannel(): Array<BaseObject> {
    const res = this.delete();
    ////////////////////////////////////////////
    //// need to set channeltipe or and rocker type in parameters?????
    // sonst werden alle gelöschten channels mit customizing wieder neu erzeugt da die parameter nicht neu gesetzt wurden
    this.setChannelType(ChannelType.Disabled);
    //button.updateAllFromKnxMetadata();

    //
    // Disable Button in Params,
    // if there is a Customizinung for this Button
    //

    return res;
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    let res = new Array<BaseObject>();

    // delete all child signals
    if (this.signals) {
      res = res.concat(this.signals.deleteAllItems());
    }

    // this.mappedChannel = undefined; // mwe 28.9.23 keep mappedChannel for undo !

    /// delete actor if autocreated and channelSize==0

    res = res.concat(super.delete());
    return res;
  }

  optimizeName(restrictOptimization: boolean) {
    // this.name = this.name.split(":")[0];

    if (this.isLogicChannel) {
      //
      // MWE 19.5.2025,
      //  Keine Namesoptimierung für Logikkanäle
      //  da kommen Bestandteile wie "Input" oder "output" vor die erhalten bleiben sollen
      //
      return;
    }

    if (this._name) {
      const orgName = this._name; // has to be _name because of function getName insite of the getter for name
      // then the originally variable name is preserved and only set if needed
      if (!restrictOptimization) {
        this.name = this._name.split("Ausgang")[0];
        this.name = this._name.split("Output")[0];

        this.name = this._name.replace("Kanal", ""); // no itx
        this.name = this._name.replace("Channel", ""); // no itx
      }

      this.name = this._name.replace("Allgemeine", ""); // no itx
      this.name = this._name.replace("Allgemein", ""); // no itx
      this.name = this._name.replace("General", "");

      this.name = this._name.replace("Einstellungen", ""); // no itx
      this.name = this._name.replace("Einstellung", ""); // no itx
      this.name = this._name.replace("Settings", ""); // no itx

      if (this._name.endsWith(":")) {
        this.name = this._name.split(":")[0];
      }

      this.name = this._name.trim();

      //
      // if we have removed anything from this channels name
      // keep the original to avoid confusion
      //
      if (this.name === "") {
        this.name = orgName;
      }
    }
  }

  getScenes(): Array<Scene> {
    const res = new Array<Scene>();

    const sceneConnections = this.getSceneConnections();
    sceneConnections.forEach(({ scene, connection }) => {
      res.push(scene);
    });

    return res;
  }

  getSceneConnections(): Array<{ scene: Scene; connection: Connection }> {
    let res = new Array<{ scene: Scene; connection: Connection }>();

    let requestChannel: Channel | undefined;

    if (this.isRequestChannel) {
      requestChannel = this;
    } else {
      requestChannel = this.mappedChannel;
    }

    if (requestChannel) {
      const consumer = requestChannel.getConnectedConsumer();
      if (consumer) {
        consumer.connections.forEach((connection) => {
          if (connection.type == ConnectionType.Scene) {
            const s = connection.getPartner(consumer);
            if (s) {
              res.push({ scene: s as Scene, connection });
            }
          }
        });
      }
    }

    res = res.sort((a, b) => a.scene.sceneNumber - b.scene.sceneNumber);

    return res;
  }

  getSceneByNumber(nr: number): Scene | undefined {
    let res: Scene | undefined = undefined;
    const scenes = this.getScenes();
    scenes.forEach((scene) => {
      if (scene.sceneNumber == nr) {
        res = scene;
      }
    });

    return res;
  }

  findPropertyByMatch(
    name: string,
    channelIndex1: number | undefined,
    sceneIndex0: number | undefined,
    matchExpressions: SceneDefinitions,
    enableScenesForChannelActive: boolean,
  ): { id: string; instanceId: string; moduleId: string } | undefined {
    function replace1(s: string, varName: string, value: string): string {
      return s.replace("${" + varName + "}", value); // no itx
    }

    let matchExpression = matchExpressions[name];

    if (matchExpression) {
      const instanceData = (this.parent as Device)?.knxInstanceData;
      const allModulesId = instanceData?.allModuleIds();

      if (allModulesId) {
        let source: string = (matchExpression as RegExp).source || (matchExpression as string);

        if (channelIndex1 != undefined) {
          source = replace1(source, "channelIndex1", channelIndex1.toString());
          source = replace1(source, "channelIndex0", (channelIndex1 - 1).toString());
        }

        if (sceneIndex0 != undefined) {
          source = replace1(source, "sceneIndex1", (sceneIndex0 + 1).toString());
          source = replace1(source, "sceneIndex0", sceneIndex0.toString());
        }

        try {
          // check if we have a valid regex
          matchExpression = new RegExp(source);
        } catch (ex: any) {
          const rule = this.parentDevice?.rules?.get(RuleIds.definitions);
          rule?.outputError(`Invalid matchExpression(${name}) :`, ex);
          return undefined;
        }

        for (const moduleId of allModulesId) {
          const extDatas = moduleId == "" ? instanceData.extData : instanceData.modules[moduleId].extData;
          for (const id in extDatas) {
            const extData = extDatas[id];
            const name: string = extData.info?.name;
            if (name) {
              const matchResult = name.match(matchExpression);
              if (matchResult) {
                return { id, instanceId: moduleId, moduleId };
              }
            }
          }
        }
      }

      if (enableScenesForChannelActive) {
        //
        // MWE 19.6.2025
        //
        // wenn lastActive false ist,
        // dann sind die Properties für die einzelnen Szenen noch "nicht aktive".
        // Das setzten der "EnableScenes" - Property oben wird zu einem ReEvaluate führen.
        // Bei dem Durchlauf nach diesem ReEvaluate wir sicher der Wert   "EnableScenes" - Property nicht mehr ändern,
        // der letzte Wert wird aus dem vorherigen Durchlauf gleichbleiben "true" sein.
        //
        // Daher ist es KEIN Fehler wenn wir hier keine Property finden
        //
        const rule = this.parentDevice?.rules?.get(RuleIds.definitions);
        rule?.outputError(`no matching Property found for matchExpression ${name}:`, matchExpression.source);
      }

      return undefined;
    }
  }

  xSetProperty(value: string, id: string, instanceId: string, scene?: Scene, setScene?: boolean) {
    const knxInstanceData = (this.parent as Device)?.knxInstanceData;
    if (knxInstanceData) {
      knxInstanceData.setValue(ValueLevels.device, value, id, instanceId, "");

      if (setScene) {
        knxInstanceData.setExtendedData(
          {
            scene: scene,
            mbe: { isSet: true },
          },
          id,
          instanceId,
        );
      }
    }
  }

  applySceneConnectionsToKnxParameters_ViaMatches(
    sceneDefinitions: SceneDefinitions,
    enableScenesForChannelActive: boolean,
  ): void {
    console.log("applySceneConnectionsToKnxParametersViaMatches", this.name);

    const knxInstanceData = (this.parent as Device)?.knxInstanceData;
    if (knxInstanceData) {
      const scenes = this.getScenes();

      const trueValue = "1";
      const falseValue = "0";

      const channelIndex0 = parseInt(this.tag);

      for (let sceneIndex0 = 0; sceneIndex0 < 8; sceneIndex0++) {
        const property_enable = this.findPropertyByMatch(
          "enable", // no itx
          channelIndex0,
          sceneIndex0,
          sceneDefinitions,
          enableScenesForChannelActive,
        );
        if (property_enable) {
          this.xSetProperty(
            sceneIndex0 < scenes.length ? trueValue : falseValue,
            property_enable.id,
            property_enable.instanceId,
          );
        }

        const property_number = this.findPropertyByMatch(
          "number", // no itx
          channelIndex0,
          sceneIndex0,
          sceneDefinitions,
          enableScenesForChannelActive,
        );
        if (property_number) {
          const scene = scenes[sceneIndex0];
          const sceneNumber = scene?.sceneNumber || 0;
          this.xSetProperty(sceneNumber.toString(), property_number.id, property_number.instanceId, scene);
        }
      }
    }
  }

  setSceneNumberInKnxParameters(
    knxProductData: KnxProductData,
    knxSceneInfo: KnxSceneInfo,
    sceneIndex: number,
    scene: Scene | undefined,
  ) {
    const sceneNumber = scene?.sceneNumber || 0;
    const active = sceneNumber > 0;

    const sceneDisabledValue = knxProductData?.sceneDisabledValue || 255;
    const [TrueEnumValue, FalseEnumValue] = getBooleanEnumValues(knxProductData, knxSceneInfo.activateId);

    //
    // Active or Deactive Scene if we have a separat swich for it
    //
    if (knxSceneInfo.activateId) {
      //
      // if we active or deactive a scene,
      // we potentaliy need to reevaluate the product data (if the params is a reEval-Parameter),
      // because the value(s) for the scene behaviour appears or disappers
      //
      this.xSetProperty(
        active ? TrueEnumValue : FalseEnumValue,
        knxSceneInfo.activateId,
        knxSceneInfo.instanceId,
        scene,
        true,
      );
    }

    //
    // set the scene number
    //

    if (knxSceneInfo.numberId) {
      let nr = -1;
      if (active) {
        nr = Number(sceneNumber) + Number(knxProductData.sceneNumberOffset);
      } else {
        nr = sceneDisabledValue;
      }

      logger.log(`applyScene nr: ${nr} ${knxSceneInfo.instanceId} ${knxSceneInfo.numberId}  `);

      this.xSetProperty(
        `${nr}`,
        knxSceneInfo.numberId,
        knxSceneInfo.instanceId,
        scene,
        true, // setScene
      );
    }
  }

  applySceneConnectionsToKnxParameters_Generic(knxInstanceData: KnxInstanceData, lastActive: boolean) {
    EP(
      "Channel.ApplySceneConnections", // no itx
    ).run(() => {
      try {
        if (isDevelopmentEnvironment()) {
          const consumer = this.getConnectedConsumer();
          if (consumer) {
            for (const connection of consumer.connections) {
              connection.knxSceneInfo = undefined;
            }
          }
        }

        const knxProductData = (this.parent as Device)?.template?.knxProductData;
        if (!knxProductData) {
          return;
        }

        const sceneConnections = this.getSceneConnections();

        for (let i = 0; i < this.knxSceneInfos.knxSceneInfos.length; i++) {
          const knxSceneInfo = this.knxSceneInfos.knxSceneInfos[i];
          const sceneConnection = sceneConnections[i];

          if (!knxSceneInfo) {
            console.log("no knxSceneInfo for sceneindex ", i);
          } else {
            this.setSceneNumberInKnxParameters(knxProductData, knxSceneInfo, i, sceneConnection?.scene);

            if (sceneConnection) {
              knxSceneInfo.device = this.parent as Device;
              sceneConnection.connection.knxSceneInfo = knxSceneInfo;
              sceneConnection.connection.raiseOnChange("knxSceneInfo");
            }
          }
        }

        //
        // if we have more sceneConnections than knxSceneInfos,
        // we have to clear the knxSceneInfo in the connection
        //

        for (let i = this.knxSceneInfos.knxSceneInfos.length; i < sceneConnections.length; i++) {
          console.log("missing: clear knxSceneInfo for sceneindex ", i);
          sceneConnections[i].connection.knxSceneInfo = undefined;
          sceneConnections[i].connection.raiseOnChange("knxSceneInfo");
        }
      } catch (ex) {
        logger.error("Error in applySceneConnectionsToKnxParameters", ex);
      }
    });
  }

  //
  // returns old value of sceneEnableProperty
  //

  applyEnableScenesToKnxParameters_ViaMatches(
    knxInstanceData: KnxInstanceData,
    sceneDefinitions: SceneDefinitions,
  ): boolean {
    console.log("applySceneConnectionsToKnxParametersViaMatches", this.name);

    const scenes = this.getScenes();

    const TrueEnumValue = "1";
    const FalseEnumValue = "0";

    const channelIndex0 = parseInt(this.tag);
    let lastActive = false;
    const property_enableForChannel = this.findPropertyByMatch(
      "enableForChannel", // no itx
      channelIndex0,
      undefined,
      sceneDefinitions,
      false,
    );
    if (property_enableForChannel) {
      lastActive =
        knxInstanceData.getValue(
          this.knxSceneInfos.channelSceneEnablingId,
          this.knxSceneInfos.channelSceneEnablingInstanceId,
        ) == TrueEnumValue;

      this.xSetProperty(
        scenes.length > 0 ? TrueEnumValue : FalseEnumValue,
        property_enableForChannel.id,
        property_enableForChannel.instanceId,
      );
    }

    return lastActive;
  }

  //
  // returns old value of sceneEnableProperty
  //

  applyEnableScenesToKnxParameters_Generic(knxInstanceData: KnxInstanceData): boolean {
    const scenes = this.getScenes();
    const scenesCount = scenes.length;

    const knxProductData = (this.parent as Device)?.template?.knxProductData;
    if (!knxProductData) {
      return false;
    }

    let sceneDisabledValue = knxProductData?.sceneDisabledValue;

    if (sceneDisabledValue == undefined) {
      sceneDisabledValue = 255;
    }

    let lastActive = false;

    try {
      let enableScenes = false;

      if (scenesCount > 0) {
        enableScenes = true;
        logger.log(`${this.name} ${this.id} : #Scenes ${scenesCount}  `);
      } else {
        enableScenes = false;
        logger.log(`NO Scenes for channel found ${this.name}`);
      }

      //
      // check if we have to enable Scenes for this channel
      //

      if (this.knxSceneInfos.channelSceneEnablingId != "") {
        const [TrueEnumValue, FalseEnumValue] = getBooleanEnumValues(
          knxProductData,
          this.knxSceneInfos.channelSceneEnablingId,
        );

        lastActive =
          knxInstanceData.getValue(
            this.knxSceneInfos.channelSceneEnablingId,
            this.knxSceneInfos.channelSceneEnablingInstanceId,
          ) == TrueEnumValue;

        this.xSetProperty(
          enableScenes ? TrueEnumValue : FalseEnumValue,
          this.knxSceneInfos.channelSceneEnablingId,
          this.knxSceneInfos.channelSceneEnablingInstanceId,
          undefined,
          false,
        );
      }
    } catch (ex: any) {
      logger.error(ex);
    }

    return lastActive;
  }

  applySceneConnectionsToKnxParameters(knxInstanceData: KnxInstanceData) {
    //
    // No Scenes for LogicChannels !!!
    //
    if (this.isLogicChannel) {
      return;
    }

    //
    // Scenes only for Lights & Rollo / Jalousie, (for now 17.6.2025 MWE)
    //

    const info = channelTypeInfo(this._channelType);
    if (!info.isLight && !info.isRollo) {
      return;
    }

    const sceneDefinitions = (this.parent as Device)?.definitions?.scenes;

    let lastActive = false;
    if (sceneDefinitions?.enableForChannel) {
      lastActive = this.applyEnableScenesToKnxParameters_ViaMatches(knxInstanceData, sceneDefinitions);
    } else {
      lastActive = this.applyEnableScenesToKnxParameters_Generic(knxInstanceData);
    }

    if (sceneDefinitions && (sceneDefinitions.enable || sceneDefinitions.number)) {
      return this.applySceneConnectionsToKnxParameters_ViaMatches(sceneDefinitions, lastActive);
    } else {
      return this.applySceneConnectionsToKnxParameters_Generic(knxInstanceData, lastActive);
    }
  }

  get parentDevice(): Device | undefined {
    const parent = this.parent as Device;
    return parent;
  }

  get fuse(): Fuse | undefined {
    const consumer = this.getConnectedConsumer();
    if (consumer) {
      return consumer.fuse;
    }
    return;
  }

  get terminal(): Terminal | undefined {
    const consumer = this.getConnectedConsumer();
    if (consumer) {
      return consumer.terminal;
    }
    return;
  }

  get consumer(): Consumer | undefined {
    const consumer = this.getConnectedConsumer();
    if (consumer) {
      return consumer;
    }
    return;
  }

  //////////////////////////////

  getPortPosition(document: Document, mainPageId: string, connection: Connection): PortInfo | undefined {
    const parent = this.parentDevice as Device;
    if (parent == undefined) {
      //
      // SystemError
      //
      logger.error(`no parent`);
      return undefined;
    } else {
      if (parent.collapseMode == 0) {
        return parent?.getPortPosition(document, mainPageId, connection);
      }
    }

    return super.getPortPosition(document, mainPageId, connection);
  }

  isConnectable_ButtonChannel(type: ConnectionType, partnerObject: ConnectableObject, checkForConnection?: Connection) {
    let res = false;
    const allowedCallNameAndChanneltypeCombinations = [
      {
        a_this: ChannelType.Blind,
        b_other: ChannelType.Jalousie,
      },
      {
        a_this: ChannelType.Jalousie,
        b_other: ChannelType.Blind,
      },
      {
        a_this: ChannelType.Switch,
        b_other: ChannelType.Dimmer,
      },
    ];
    if (
      channelTypeInfo(this.channelType).temperatureAndHeatingChannelTypes &&
      partnerObject.channelType == ChannelType.Heating
    ) {
      return true;
    }
    // if (
    //   this.channelType != ChannelType.TemperatureMeasurement &&
    //   channelType == ChannelType.Heating
    // ) {
    //   return true;
    // }

    // if (
    //   this.channelType != ChannelType.TemperatureSetpoint &&
    //   channelType == ChannelType.Heating
    // ) {
    //   return true;
    // }

    if (
      this.channelType == ChannelType.Generic &&
      channelTypeInfo(partnerObject.channelType).canBeConnectedWithButtonChannel
    ) {
      return true;
    }

    allowedCallNameAndChanneltypeCombinations.forEach((comb) => {
      if (comb.a_this == this.channelType && comb.b_other == partnerObject.channelType) {
        res = true;
      }
    });
    if (res) {
      return res;
    }

    if (partnerObject.className == "Multiplexer") {
      if (partnerObject.channelType == ChannelType.Generic) {
        return true;
      }
      return isSameLightRolloHeating(this.channelType, partnerObject.channelType) != undefined;
    }

    if (this.channelType != partnerObject.channelType) {
      return false;
    }

    if (this.channelType == ChannelType.Scene && !checkForConnection) {
      if (this.isConnected(type)) {
        return false;
      }
    }
    if (partnerObject.className == "ButtonChannel") {
      return false;
    }
    // check if connection between channel and consumer already present
    res = true;
    if (!checkForConnection) {
      if (this.channelType == ChannelType.Scene && this.connections.size > 0) {
        return false;
      }
      this.connections.forEach((connection) => {
        if (connection.fromId == partnerObject.id || connection.toId == partnerObject.id) {
          res = false;
        }
      });
    }

    return res;
  }

  isConnectable(type: ConnectionType, partnerObject: ConnectableObject, checkForConnection?: Connection): boolean {
    if (AllowedConnections.isConnectionAllowed(this, partnerObject, true)) {
      if (partnerObject.className == "Channel") {
        return false;
      }

      if (this.parent?.className != "Button" && partnerObject.className == "Button") {
        return false;
      }
      // check for same channel type or dali
      if (electricalChannelType(this.channelType) != electricalChannelType(partnerObject.channelType) || this.isDali != partnerObject.isDali) {
        if (this.isDali || partnerObject.isDali) {
          return channelTypeInfo(this.channelType).isLight && channelTypeInfo(partnerObject.channelType).isLight;
        }
        // rollo and jalousie can be connected
        if (channelTypeInfo(this.channelType).isRollo && channelTypeInfo(partnerObject.channelType).isRollo) {
          return true;
        }
        // a Lan actor channel (channelType Lan) also accepts Lan2 consumers (e.g. a double LAN socket)
        if (channelTypeInfo(this.channelType).isLan && channelTypeInfo(partnerObject.channelType).isLan) {
          return true;
        }
        if (this.channelType != ChannelType.Generic) {
          return electricalChannelType(this.channelType) == electricalChannelType(partnerObject.channelType);
        }
      }
      return true;
    }
    return false;

    if (this.parent?.className == "Button") {
      return this.isConnectable_ButtonChannel(type, partnerObject, checkForConnection);
    }

    if (type != ConnectionType.Channel && type != ConnectionType.FromSourceDevice) {
      return false;
    }

    if (!areChannelTypesCompatible(this.channelType, partnerObject.channelType, this.parent as Device)) {
      return false;
    }

    return checkForConnection != undefined || this.connections.size == 0;
  }

  getHtmlElement() {
    return document.getElementById(this.id);
  }

  ////////////////////////////
  getConnectedObject(className: string): ConnectableObject | undefined {
    let res: ConnectableObject | undefined = undefined;

    let requestChannel: Channel | undefined = this;
    if (!requestChannel.isRequestChannel) {
      requestChannel = requestChannel.requestChannel;
    }

    if (requestChannel) {
      requestChannel.connections.forEach((connection) => {
        const partner = connection.getPartner(requestChannel!);
        if (partner?.className == className) {
          res = partner as Consumer;
        }
      });
    }

    return res;
  }

  getConnectedConsumer(): Consumer | undefined {
    return this.getConnectedObject("Consumer") as Consumer; // no itx
  }

  // mwe 5.4.2024:
  // diess function macht nur für ButtonChannels Sinn
  // Actor sind fast immer mir mehreren Scenen verbunden
  // In ButtonChannel ist sie auch schon lange so implementiert
  getConnectedScene(): Scene | undefined {
    return this.getConnectedObject("Scene") as Scene; // no itx
  }

  ////////////////////////////

  getAllConnectedConsumers(): Array<Consumer> {
    const res = new Array<Consumer>();
    let requestChannel: Channel | undefined = this;

    if (!requestChannel.isRequestChannel) {
      requestChannel = requestChannel.mappedChannel;
    }

    if (requestChannel) {
      requestChannel.connections.forEach((connection) => {
        const partner = connection.getPartner(requestChannel!);
        if (partner?.className == "Consumer") {
          res?.push(partner as Consumer);
        }
      });
    }
    res.sort((a, b) => a.tag.localeCompare(b.tag));
    return res;
  }

  getAllConnectedMultiplexers(): Array<Multiplexer> {
    const res = new Array<Multiplexer>();
    let requestChannel: Channel | undefined = this;

    if (!requestChannel.isRequestChannel) {
      requestChannel = requestChannel.mappedChannel;
    }

    if (requestChannel) {
      requestChannel.connections.forEach((connection) => {
        const partner = connection.getPartner(requestChannel!);
        if (partner?.className == "Multiplexer") {
          res?.push(partner as Multiplexer);
        }
      });
    }
    res.sort((a, b) => a.tag.localeCompare(b.tag));
    return res;
  }

  getAllConnectedObjects(): Array<ConnectableObject> {
    const res = new Array<ConnectableObject>();
    let requestChannel: Channel | undefined = this;

    if (!requestChannel.isRequestChannel) {
      requestChannel = requestChannel.mappedChannel;
    }

    if (requestChannel) {
      requestChannel.connections.forEach((connection) => {
        const partner = connection.getPartner(requestChannel!);
        if (partner) {
          res?.push(partner as Consumer);
        } else {
          logger.error(`Invalid Connection without partner found: channel: ${this.fullTag} ${this.name}`);
        }
      });
    }

    res.sort((a, b) => a.tag.localeCompare(b.tag));
    return res;
  }

  getConnectedConsumersOrMultiplexerforButton(): Array<Consumer> {
    const res = new Array<Consumer>();
    logger.log(`getConnectedConsumersOrMultiplexerforButton ${this.connections.size}`);

    let requestChannel: Channel | undefined = this;
    if (!requestChannel.isRequestChannel) {
      requestChannel = requestChannel.mappedChannel;
    }

    if (requestChannel) {
      requestChannel.connections.forEach((connection) => {
        if (connection.type === ConnectionType.Button || connection.type === ConnectionType.Multiplexer) {
          const c = connection.getPartner(requestChannel!) as Consumer;
          if (c) {
            res.push(c);
          }
        }
      });
    }

    return res;
  }

  getConnectedActorChannels(): Array<Channel> | undefined {
    const consumers = this.getConnectedConsumersOrMultiplexerforButton();
    const res = new Array<Channel>();

    if (consumers) {
      consumers.forEach((consumer) => {
        const actorChannel = consumer.getConnectedChannel();
        if (actorChannel) {
          res.push(actorChannel);
        }
      });
    }

    if (res.length > 0) {
      return res;
    } else {
      return undefined;
    }
  }

  getSceneConnection(scene: Scene): Connection | undefined {
    let res: Connection | undefined;

    const consumer = this.getConnectedConsumer();
    consumer?.connections.forEach((connection) => {
      const partner = connection.getPartner(consumer);
      if (partner == scene) {
        res = connection;
        return; // from forEach
      }
    });

    return res;
  }

  getSceneAndInfos(): Array<{
    knxSceneInfo: KnxSceneInfo | undefined;
    scene: Scene;
  }> {
    const res = new Array<{
      knxSceneInfo: KnxSceneInfo | undefined;
      scene: Scene;
    }>();

    const consumer = this.getConnectedConsumer();
    consumer?.connections.forEach((connection) => {
      const partner = connection.getPartner(consumer);
      if (partner?.className === "Scene") {
        res.push({
          knxSceneInfo: connection.knxSceneInfo,
          scene: partner as Scene,
        });
      }
    });

    return res;
  }

  getSignalByType(type: SignalType): Signal | undefined {
    let res: any = undefined;
    this.signals.forEach((signal) => {
      if (signal.type.equalsExtended(type)) {
        res = signal;
      }
    });
    return res;
  }

  getSignalsByType(type: SignalType): Array<Signal> {
    const res = new Array<Signal>();
    this.signals.forEach((signal) => {
      if (signal.type.equalsExtended(type)) {
        res.push(signal);
      }
    });
    return res;
  }

  getSignalByNumber(number: string): Signal | undefined {
    const res = this.signals.findFirst((signal) => signal.number == number);
    return res;
  }

  getOrCreateSignalByType(type: SignalType): Signal | undefined {
    this.assetIsRequestChannel();

    let res = this.getSignalByType(type);

    if (!res) {
      const knxSignal = this.knxChannel?.getSignalByType(type);
      if (knxSignal) {
        const copyOfSignal = new Signal({}, this);
        this.signals.add(copyOfSignal);

        copyOfSignal.tag = knxSignal.tag;
        copyOfSignal.instanceId = knxSignal.instanceId;
        copyOfSignal.name = knxSignal.name;
        copyOfSignal.type = knxSignal.type;
        copyOfSignal.number = knxSignal.number;
        copyOfSignal.knxId = knxSignal.knxId;
        copyOfSignal.knxFunctionText = knxSignal.knxFunctionText;

        res = copyOfSignal;
      }
    }

    return res;
  }

  //
  // returns true if a name has changed
  //
  // setNameFromConnections(): boolean {
  //   if (this.manualName) {
  //     return false;
  //   }

  //   return false;

  //   this.assetIsRequestChannel();

  //   if (
  //     this.channelType == ChannelType.General ||
  //     this.channelType == ChannelType.Unknown ||
  //     this.channelType == ChannelType.Environment
  //   ) {
  //     return false;
  //   }

  //   const isActor = (this.parent as Device)?.isActor;
  //   var functionName = Channel.functionNameForNotConnected;

  //   if (isActor) {
  //     this.assetIsActorChannel();
  //     //
  //     // is a Actor
  //     //
  //     const consumer = this.getConnectedConsumer();
  //     if (consumer) {
  //       functionName = `${consumer.fullTag} ${consumer.name}`;

  //       //
  //       // set _name directly instead of name
  //       // to bypass "set name()""
  //       // to avoid setting "this.manualName" to true
  //       //
  //       const changed = this._name != functionName;
  //       this._name = functionName;

  //       return changed;
  //     }
  //   } else {
  //     //
  //     // MWE 30.7.2024
  //     // Button Channel Names are calulated now automaticly via nameOrConnectedNames
  //     //
  //   }

  //   return false;
  // }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  //
  // Mache aus einem GenericValue-Signal ggfls ein Swicht-Signal
  //
  // Wird verwendet wenn der Benutzer einen Button Type (Switch) gesetzt hat
  // Das Gerät aber nur neutrale Values unterstützt U(zB Jung 4074TSM)
  //
  applyChannelTypeFromRequestChannel(channelType: ChannelType) {
    this.assetIsKnxChannel();

    this.signals.forEach((signal) => {
      if (signal.type.name == SignalTypes.GenericValue.name) {
        switch (channelType) {
          case ChannelType.Switch:
            signal.type = SignalTypes.Switch;
            this.channelType = channelType;
            break;
          default:
            logger.log("applyChannelTypeFromRequestChannel channelType not supported", channelType);
        }
      }
    });
  }

  applyScripts() {
    // is overwritten in ButtonChannel
  }

  getKnxChannelTypePropertyValue(): ChannelType {
    const device = this.parent as Device;
    if (device) {
      let knxChannel: Channel | undefined = this;

      if (this.isRequestChannel) {
        knxChannel = this.knxChannel;
      }

      if (knxChannel && knxChannel.knxChannelTypeProperty) {
        const v: string = device.knxInstanceData.getValue(
          knxChannel.knxChannelTypeProperty.id,
          knxChannel.knxChannelTypeProperty.instanceId,
        );

        let res: ChannelType | undefined;

        knxChannel.knxChannelTypeProperty.mapChannelTypeToValue.forEach((value, key) => {
          if (value.optionValue == v) {
            res = key as ChannelType;
          }
        });

        if (res) {
          return res;
        }
      }
    }

    //
    // if the current propertyValue is not matching one of the known values,
    // the user has choosen a "special Value" like "send Value"
    //
    // This is legal.
    // Sometimes the user (or our scripts) has to overwrite our set value for ie "Swich"
    // with "somthing more special", witch acts for estada "as Switch"
    //

    return ChannelType.Unknown;
  }

  setChannelTypePropertyValue_ByScript(
    device: Device,
    knxChannelTypeProperty: any | undefined,
    newChannelType: ChannelType,
  ): boolean {
    const mapChannelTypeConfig = device.mapChannelTypeConfig;
    // special channels ( special channels can be set via value)

    if (mapChannelTypeConfig?.props) {
      let anyWritten = false;
      for (const prop of mapChannelTypeConfig.props) {
        let newPropertyValue = prop.mapping[newChannelType];

        if (newPropertyValue == undefined) {
          newPropertyValue = prop.mapping["Default"];
        }

        if (newPropertyValue == undefined) {
          const nearstChannelType = this.searchNearstChannelType(newChannelType, device, this.knxChannel);
          if (nearstChannelType) {
            newPropertyValue = prop.mapping[nearstChannelType];
          }
        }

        let id = "";
        let instanceId = knxChannelTypeProperty?.instanceId || this.knxChannelId || "";

        if (typeof prop.id === "function") {
          try {
            id = prop.id(this.tag);
            instanceId = "";
          } catch (ex) {
            if (isUiModeProductAdmin()) {
              DebugOutput.outputError("id", ex);
            }
            console.error("Error in id function", ex);
          }
        } else {
          id = prop.id;
        }

        if (prop.instanceId) {
          if (typeof prop.instanceId === "function") {
            try {
              instanceId = prop.instanceId(this.tag);
            } catch (ex) {
              if (isUiModeProductAdmin()) {
                DebugOutput.outputError("instanceId2", ex);
              }
              console.error("Error in instanceId function", ex);
            }
          } else {
            DebugOutput.outputError("instanceId", "instanceId is not a function");
            console.error("instanceId is not a function");
          }
        }

        if (newPropertyValue != undefined) {
          const oldValue = device.knxInstanceData.getValue(id, instanceId, true);
          if (oldValue != newPropertyValue) {
            //
            // MWE 17.7.2026
            //
            // ab heute setzen wir den channeltype nur noch auf level estadaUi (3.8),
            // damit der User den Wert in den Parametern überschreiben kann.
            //
            // Damit würde aber werte die in frühern Version geschrieben wurden,
            // nicht mehr überschrieben werden könne.
            // Dehalb unterscheide ich hier ob die ChannelTypeProperty
            // mit einer alten version geschrieben wurde,
            // und erzwinge dann EINMALIG das überschreiben mit force.
            // beim nächsten eval ist dann device.eV schon auf 2 oder größer gesetzt.
            //

            let level = ValueLevels.estadaUi;
            if (device.eV < 2) {
              level += ValueLevels.force;
            }

            device.knxInstanceData.setValue(level, newPropertyValue, id, instanceId, "");
            device.knxInstanceData.setExtendedData({ mbe: { isSet: true } }, id, instanceId);
            //
            // ChannelType changes force allway a reevaluation
            // even if the parameter is not detected as "reeval-Parameter"
            // because it is part only part of a calculation
            //
            device.reEvalKnxMetadataNeeded = true;
          }
          anyWritten = true;
        }
      }

      return anyWritten;
    }

    return false;
  }

  searchNearstChannelType(
    newChannelType: ChannelType,
    device: Device,
    knxChannel: Channel | undefined,
  ): ChannelType | undefined {
    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    if (newChannelType == ChannelType.Switch) {
      //
      // wenn ein Button den type Switch NICHT unterstützt, aber den Type SendValue (zB JUNG 4074TSM)
      // dann setzen wir die Propertzy auf SenValue
      //
      const res = this.searchNearstChannelType1([ChannelType.GenericValue], newChannelType, device, knxChannel);
      return res;
    }

    if (newChannelType == ChannelType.Dimmer) {
      //
      // if we have a reqirement for a "dim button",
      // but the button only supports "switch"
      // then we downgrade to "switch"
      //
      const res = this.searchNearstChannelType1([ChannelType.Switch], newChannelType, device, knxChannel);
      return res;
    }

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // Handle Jalousie and Blind "as one type"
    //
    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    const jalousieBlindMappingOrder = [ChannelType.Jalousie, ChannelType.Blind];
    let res = this.searchNearstChannelType1(jalousieBlindMappingOrder, newChannelType, device, knxChannel);
    if (res !== undefined) {
      return res;
    }

    const heatingMappingOrder = [
      ChannelType.Heating,
      ChannelType.TemperatureMeasurement,
      ChannelType.TemperatureSetpoint,
      ChannelType.OperationMode,
    ];
    res = this.searchNearstChannelType1(heatingMappingOrder, newChannelType, device, knxChannel);
    if (res !== undefined) {
      return res;
    }

    return undefined;
  }

  searchNearstChannelType1(
    similarChannelTypes: Array<ChannelType>,
    newChannelType: ChannelType,
    device: Device,
    knxChannel: Channel | undefined,
  ): ChannelType | undefined {
    if (similarChannelTypes.includes(newChannelType)) {
      //
      // der newChannelType is teil der Ähnlichkeitsliste
      //
      // Ähnlichkeitsliste ist zuglich eine Pprioritäten-Liste,
      // in der die ähnlichen ChannelTypes verwednet werden sollen
      //

      const mapChannelTypeConfig = device.mapChannelTypeConfig;
      if (mapChannelTypeConfig?.supportedChannelTypes) {
        //
        // use "supportedChannelTypes" from script
        //
        for (const channelType of similarChannelTypes) {
          const found = mapChannelTypeConfig.supportedChannelTypes[channelType];
          if (found) {
            return channelType;
          }
        }
        //
        // we have a mapping in the script, but no suitable channel type was found
        //
        return undefined;
      }

      //
      // we have NO ChannelTypes from script
      // use knxChannel.knxChannelTypeProperty is exists
      //

      if (knxChannel?.knxChannelTypeProperty) {
        for (const channelType of similarChannelTypes) {
          //
          // use automaticly in KnxParameterViewer detected channel type mapping
          //
          const found = knxChannel.knxChannelTypeProperty.mapChannelTypeToValue.get(channelType);
          if (found) {
            return channelType;
          }
        }
      }
    }
    return undefined;
  }

  setKnxChannelTypePropertyValue(newChannelType: ChannelType, force: boolean) {
    const device = this.parent as Device;
    if (device) {
      let requestChannel: Channel | undefined = this;
      let knxChannel: Channel | undefined = this;

      if (this.isRequestChannel) {
        knxChannel = this.knxChannel;
      } else {
        requestChannel = this.requestChannel;
      }
      // special channels ( special channels can be set via value)
      if (knxChannel?.tag.startsWith("TX_") || requestChannel?.tag.startsWith("TX_")) {
        return;
      }
      if (knxChannel) {
        if (knxChannel.knxChannelTypeProperty || device.template?.knxProductData.hasScript_mapChannelType) {
          if (this.setChannelTypePropertyValue_ByScript(device, knxChannel.knxChannelTypeProperty, newChannelType)) {
            return;
          }
        }
      } else {
        if (device.template?.knxProductData.hasScript_mapChannelType) {
          this.setChannelTypePropertyValue_ByScript(device, undefined, newChannelType);
          return;
        }
      }

      const knxChannelTypeProperty = knxChannel?.knxChannelTypeProperty;
      if (knxChannelTypeProperty == undefined) {
      }

      let isSet = false;
      if (requestChannel && knxChannel && knxChannel.knxChannelTypeProperty) {
        let valueFromEnum = knxChannel.knxChannelTypeProperty.mapChannelTypeToValue.get(newChannelType)?.optionValue;
        if (valueFromEnum == undefined) {
          const nearstChannelType = this.searchNearstChannelType(newChannelType, device, this.knxChannel);
          if (nearstChannelType) {
            valueFromEnum = knxChannel.knxChannelTypeProperty.mapChannelTypeToValue.get(nearstChannelType)?.optionValue;
          }
        }

        /////////////////////////////////////////////////////////////////////////////////////////////////////////
        if (valueFromEnum != undefined) {
          device.knxInstanceData.setExtendedData(
            { mbe: { isSet: true } },
            knxChannel.knxChannelTypeProperty.id,
            knxChannel.knxChannelTypeProperty.instanceId,
          );

          logger.log(`applyChannelType ${this.name} : ${valueFromEnum}`);

          if (force) {
            device.knxInstanceData.setValue(
              ValueLevels.delete,
              valueFromEnum,
              knxChannel.knxChannelTypeProperty.id,
              knxChannel.knxChannelTypeProperty.instanceId,
              knxChannel.knxChannelTypeProperty.moduleId,
            );
          }

          device.knxInstanceData.setValue(
            ValueLevels.device,
            valueFromEnum,
            knxChannel.knxChannelTypeProperty.id,
            knxChannel.knxChannelTypeProperty.instanceId,
            knxChannel.knxChannelTypeProperty.moduleId,
          );
          isSet = true;
        }

        device.knxInstanceData.setExtendedData(
          { mbe: { isSet: isSet } },
          knxChannel.knxChannelTypeProperty.id,
          knxChannel.knxChannelTypeProperty.instanceId,
        );
      }
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////

  //
  // returns true if a Parameter is found
  //
  applyFuctionNamesToKnxParameters() {
    this.assetIsKnxChannel();

    if (
      this.channelType == ChannelType.General ||
      this.channelType == ChannelType.Unknown ||
      this.channelType == ChannelType.Environment
    ) {
      return false;
    }

    const device = this.parent as Device;

    const knxProductData = device.template?.knxProductData;

    if (this.knxChannelDescriptionProperty) {
      const requestChannel = this.requestChannel;

      //
      // cleanup code for old data ...
      //
      //   Actors dont have manual managed channel names !!!!!
      //   The names have been set by earlier version with the connected consumer.
      //      So we need to clear it,
      //      to make sure "nameOrConnectedNames" may deliver the correct result
      //

      if (requestChannel && (this.parent as BaseModel).isActor) {
        requestChannel.name = "";
      }

      let functionName = requestChannel?.nameOrConnectedNames;

      if (requestChannel?.channelType == ChannelType.Environment) {
        functionName = state.translate("channelType.Environment", "Environment");
      } else {
        if (requestChannel && (this.parent as BaseModel).isActor && this.consumer) {
          functionName = `${LINK_CHAR}${this.consumer.fullTag} ${this.consumer.name}`;
        }
      }

      device.knxInstanceData.setExtendedData(
        { mbe: { isSet: true } },
        this.knxChannelDescriptionProperty.id,
        this.knxChannelDescriptionProperty.instanceId,
      );

      logger.log(`applyFuctionNamesToKnxParameters ${this.name} : ${functionName}`);

      if (!functionName) {
        functionName = "-/-";
      }

      if (functionName) {
        if (knxProductData) {
          if (knxProductData.currentRules && knxProductData.hasScript_mapSignalToTag) {
            const mapChannelTextConfig = knxProductData.currentRules.mapChannelText(device, this, functionName);
            if (mapChannelTextConfig?.props) {
              for (const prop of mapChannelTextConfig.props) {
                let id = "";
                if (typeof prop.id === "function") {
                  id = prop.id(this.tag);
                } else {
                  id = prop.id;
                }
                let instanceId = "";
                if (typeof prop.instanceId === "function") {
                  instanceId = prop.instanceId(this.tag);
                } else {
                  instanceId = prop.instanceId || "";
                }

                device.knxInstanceData.setValue(ValueLevels.device, functionName, id, instanceId, "");
              }

              return;
            }
          }
        }

        device.knxInstanceData.setValue(
          ValueLevels.device,
          functionName,
          this.knxChannelDescriptionProperty.id,
          this.knxChannelDescriptionProperty.instanceId,
          this.knxChannelDescriptionProperty.moduleId,
        );
      }
    } else {
      logger.log(`no knxChannelDescriptionProperty for channel: ${this.fullTag} ${this.name} `);
    }
  }

  getRequestSignal(signal: Signal): Signal | undefined {
    if (this.isRequestChannel) {
      logger.error("Channel is RequestChannel");
      return signal;
    }

    const requestChannelChannel = this.mappedChannel;
    return requestChannelChannel?.getSignalByType(signal.type);
  }

  get firstSignalNumber(): number {
    if (this.signals.size == 0) {
      return -1;
    }

    let res = parseInt(this.signals.asArray()[0].number);
    this.signals.forEach((signal) => {
      if (parseInt(signal.number) < res) {
        res = parseInt(signal.number);
      }
    });
    return res;
  }

  //////////////////////////////////////////////////////
  ///////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    const res = new Array<BaseModel>();
    this.connections.forEach((connection) => {
      res.push(connection);
    });
    return res;
  }
  //////////////////////////////////////////////////////
  ///////////// for paste
  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      this.signals.mapAfterCopy();

      this.mappedId = state.objectMapping.getMappedId(this.mappedId);
    }
  }

  onSelect(newValue: boolean) {
    if (this.selected && newValue == false) {
      this.parent?.raiseOnChange("unselectChild");
    }
  }

  //
  // Channels dont have tags !!!
  //   for fullTag with use the shortName of the KnxChannel
  //
  getFullTagParts(): Array<any> {
    let res = super.getFullTagParts();

    const device = this.parent as Device;
    if (device) {
      res = device.getFullTagParts();
      res.push({ prefix: ".", tag: this.tag3 });
    }

    return res;
  }

  get tag2(): string {
    let p = this.parent as BaseModel;
    p = p.parent?.parent as BaseModel;
    if (p) {
      return this.getRelativeTag(p);
    }

    return this.tag;
  }

  get tag3(): string {
    const devive = this.parent as Device;
    if (devive) {
      const knxProductData = devive.template?.knxProductData;
      if (knxProductData && (knxProductData.hasScript_mapSignalToTag || knxProductData.hasScript_mapSignalToTag)) {
        return this.tag;
      } else {
        return this.shortName2 || this.tag;
      }
    }

    return this.tag;
  }

  get shortName2(): string {
    let shortName2 = this.name.trim();

    let knxChannel: Channel = this;
    let requestChannel: Channel = this;

    if (this.isRequestChannel && this.mappedChannel) {
      knxChannel = this.mappedChannel;
    } else {
      requestChannel = this.mappedChannel || this;
    }

    if (knxChannel) {
      shortName2 = knxChannel.name;
    }

    // a LAN actor channel's name is already a plain number (or "1,2" for a Lan2 double-slot channel);
    // nameToShortName() would otherwise cut it off at the comma
    if (channelTypeInfo((this.parent as Device)?.channelType).isLan) {
      return shortName2;
    }

    //
    // reduce shortname by the word "channel" or "Kanal" etc in the beginning
    //

    shortName2 = nameToShortName(shortName2);
    return shortName2;
  }

  get shortName(): string {
    let shortName = this.shortName2;

    //// reduce shortname by name of connected consumer or scene

    let knxChannel: Channel = this;
    let requestChannel: Channel = this;

    if (this.isRequestChannel && this.mappedChannel) {
      knxChannel = this.mappedChannel;
    } else {
      requestChannel = this.mappedChannel || this;
    }

    const partners = requestChannel.getAllConnectedObjects();

    if (partners) {
      let connectionText = "";

      //
      //
      // reduce channelName by targetName
      //
      //

      if (partners.length > 1) {
        const i = 1;
        for (let i = 0; i < partners.length; i++) {
          const partner = partners[i];
          connectionText += `${partner.getRelativeTag(this)} ${partner.name}`.trim();

          if (i < partners.length - 1) {
            connectionText += ", ";
          }
        }
      } else if (partners.length == 1) {
        const partner = partners[0];
        if (partner.className == "Scene") {
          connectionText = `${partner.name}`;
          shortName = shortName.replace(connectionText, "");
        } else {
          const replaceText1 = `${partner.fullTag}`;
          shortName = shortName.replace(replaceText1, "");

          const replaceText2 = `${partner.name}`;
          shortName = shortName.replace(replaceText2, "");
        }
      }
    }

    return shortName.trim();
  }

  mergeChannelDataToMe(fromChannel: Channel) {
    // const allSignals1 = this.getAllSignalsOfAllSubChannels();

    logger.log(`mergeOneSubChannelToMe to=${this.name} from=${fromChannel.name}`);

    //
    // merge signals
    //
    fromChannel.signals.forEach((signal) => {
      signal.parent = this;
      this.signals.addWithoutUpdate(signal);
    });
    fromChannel.signals.items.clear();

    logger.log(`merged signals to=${this.name} from=${fromChannel.name} #${this.signals.size}`, this.rawSceneData);

    //
    // merge knxChannelDescriptionProperty
    //

    this.knxChannelDescriptionProperty ||= fromChannel.knxChannelDescriptionProperty;
    this.knxChannelTypeProperty ||= fromChannel.knxChannelTypeProperty;

    logger.log(
      `merge channelSceneEnablingId ${fromChannel.knxSceneInfos.channelSceneEnablingId} to ${this.knxSceneInfos.channelSceneEnablingId}`,
    );
  }

  mergeSubChannelsToMe(): Array<Channel> {
    logger.log(`mergeSubChannelsToMe ${this.name}`);

    let flatChannelArray = new Array<Channel>();
    flatChannelArray.push(this);

    if (this.subChannels) {
      // const subChannels = this.subChannels;

      let numberOfChannelsWithChannelDescriptionProperty = 0;
      this.subChannels.forEach((subChannel) => {
        if (subChannel.knxChannelDescriptionProperty) {
          numberOfChannelsWithChannelDescriptionProperty++;
        }
      });

      // a knxChannelDescriptionProperty is a clear sign for a mainChannel
      //
      // if we habe more of them,
      //   then the child Channels should be treated as mainChannels

      if (numberOfChannelsWithChannelDescriptionProperty <= 1) {
        for (let i = 0; i < this.subChannels.length; i++) {
          const subChannel = this.subChannels[i];
          const nestedMainChannelList = subChannel.mergeSubChannelsToMe();
          for (const nestedMainChannel of nestedMainChannelList) {
            this.mergeChannelDataToMe(nestedMainChannel);
          }
        }
        this.subChannels = [];
      } else {
        logger.log(
          `mergeSubChannelsToMe ${this.name} keep all subchannels ${numberOfChannelsWithChannelDescriptionProperty}`,
        );
        this.subChannels.forEach((subChannel) => {
          flatChannelArray = flatChannelArray.concat(subChannel.mergeSubChannelsToMe());
        });
      }
    }
    return flatChannelArray;
  }

  mergeKnxChannelPropertyX(sourceObj: Signal | Channel | undefined) {
    this.mergeKnxChannelProperty("knxChannelDescriptionProperty", sourceObj); // no itx
    this.mergeKnxChannelProperty("knxChannelTypeProperty", sourceObj); // no itx
    this.mergeKnxChannelProperty("knxButtonSceneNumberProperty", sourceObj); // no itx
    this.instanceId ||= sourceObj?.instanceId;
    if (this.knxChannelTypeProperty) {
      this.knxChannelTypeProperty.knxChannel = this;
    }
  }

  mergeKnxChannelProperty(
    propertyName: "knxChannelDescriptionProperty" | "knxChannelTypeProperty" | "knxButtonSceneNumberProperty", // no itx
    obj: Signal | Channel | undefined,
  ) {
    if (this.tag == "TX_I" || this.tag == "TX_S") {
      // for special signal channel &&
      // ignored siganls no channel type property available/used
      return;
    }

    const knxChannelProperty = (obj as any)[propertyName];

    if (knxChannelProperty) {
      //remove to prevent doubling
      (obj as any)[propertyName] = undefined;
      if ((this as any)[propertyName]) {
        if (
          (this as any)[propertyName].id != knxChannelProperty.id ||
          (this as any)[propertyName].instanceId != knxChannelProperty.instanceId
        ) {
          logger.error(`conflicting knxChannelDescriptionProperty channel:${this.name} }`);
        }
      } else {
        (this as any)[propertyName] = knxChannelProperty;
      }
    }
  }

  hasRawSceneDataItem(checkItem: RawSceneDataItem): boolean {
    const found = this.rawSceneData?.find((item) => item.id == checkItem.id);
    return found != undefined;
  }

  get isStatusChannel(): boolean {
    if (this.parentDevice?.isActor) {
      return false;
    }

    if (this.isActorChannel) {
      return false;
    }

    const knxChannel = this.isRequestChannel ? this.knxChannel : this;
    if (this.isButtonChannel && knxChannel?.isCreatedFromScript) {
      return this.tag.startsWith("S");
    }

    if (!this.isRequestChannel) {
      const requestChannel = this.requestChannel;
      if (requestChannel?.tag.startsWith("S")) {
        return true;
      }
    } else {
      if (this.tag.startsWith("S")) {
        return true;
      }
    }

    return channelTypeInfo(this.channelType).isStatusChannel;
  }

  get isActorChannel(): boolean {
    return true;
  }

  get isLogicChannel(): boolean {
    return this.isLogicInputChannel || this.isLogicOutputChannel;
  }

  get isLogicInputChannel(): boolean {
    return this.tag.startsWith("AL");
  }

  get isLogicOutputChannel(): boolean {
    return this.tag.startsWith("TL");
  }

  assetIsKnxChannel(): boolean {
    if (this.isRequestChannel) {
      logger.error("Channel is not a knxChannel");
      return false;
    }
    return true;
  }

  assetIsRequestChannel(): boolean {
    if (!this.isRequestChannel) {
      logger.error("Channel is not a requestChannel");
      return false;
    }
    return true;
  }

  assetIsActorChannel(): boolean {
    return true;
  }

  assetIsButtonChannel(): boolean {
    logger.error("Channel is not a ButtonChannel");
    return false;
  }

  connect(page: Page, partner: ConnectableObject): Connection {
    const c = new Connection({}, page);
    c.type = this.isActorChannel ? ConnectionType.Channel : ConnectionType.Button;
    /// from = consumer
    c.fromId = partner.id;

    /// to = channel
    c.toId = this.id;

    c.addToConnectableObjects();
    c.addToPage();

    this.raiseOnChange();

    // allow redraw, to get correct port position in collapsed mode
    setTimeout(() => {
      c.updateEndPoints();
    }, 100);

    return c;
  }

  onConnect(connection: Connection): void {
    this.updateParametersAfterConnectionStateChanged(connection);
  }

  onDisconnect(connection: Connection): void {
    this.updateParametersAfterConnectionStateChanged(connection);
  }

  raiseOnChange(changedProperty: string | any | undefined = undefined, eventSource?: string, uiOnly?: boolean) {
    super.raiseOnChange(changedProperty, eventSource, uiOnly);
    if (changedProperty == "connection") {
      this.parent?.raiseOnChange(changedProperty, eventSource, uiOnly);
    }
  }

  isParameterChannelTypeDifferent(): boolean {
    logger.error("isParameterChannelTypeDifferent is only implemented for ButtonChannel");
    return false;
  }

  setChannelType(newValue: ChannelType) {
    if (newValue == ChannelType.TemperatureMeasurement || newValue == ChannelType.TemperatureSetpoint) {
      newValue = ChannelType.Heating;
    }

    this._channelType = newValue;
  }

  autoSetChannelType(channelType: ChannelType): boolean {
    //
    // AktorChannels in buttons dont automaticly set ChannelTypes! MWE 21.4.2025
    //
    if (this.isButtonChannel && this.isActorChannel) {
      return false;
    }

    // special channels ( special channels can be set via value)
    if (this?.tag.startsWith("TX_") || this.knxChannel?.tag.startsWith("TX_")) {
      return false;
    }

    let allow = false;
    const device = this.parent as Device;
    if (this.connections.size == 0 && device) {
      if (device.template?.knxProductData.hasScript_setChannelType) {
        //
        // KnxChannelType will set by a script,
        // so is allowed to controll it by the consumer
        //
        allow = true;
      }

      if (this.knxChannel?.knxChannelTypeProperty || device.template?.knxProductData.hasScript_mapChannelType) {
        //
        // KnxChannelType will set by the known "knxChannelTypeProperty" or by a script,
        // so is allowed to controll it by the consumer
        //
        allow = true;
      }
    }

    if (allow) {
      if (channelType == ChannelType.Heating && this.channelType == ChannelType.OperationMode) {
        //
        // MWE 28.6.2025
        //
        // if the channelType is OperationMode, and we connect it with a heating consumer....,
        //
        // we keep the channelType OperationMode,
        // as we want to allow the connection of an operation mode button with a heating consumer.
        //
        return true;
      }

      this.channelType = channelType;
      if (this.parentDevice) {
        this.setChannelTypePropertyValue_ByScript(this.parentDevice, this.knxChannelDescriptionProperty, channelType);
      }

      this.raiseOnChange("channelType");
      return true;
    } else {
      return false;
    }
  }

  updateParametersAfterConnectionStateChanged(connection: Connection) {
    this.assetIsRequestChannel();

    // if (this.setNameFromConnections()) {

    const device = this.parent as Device;
    if (device) {
      if (device.template?.knxProductData.currentRules) {
        device.template.knxProductData.currentRules.pullAllValues(device, this);
      }

      const knxChannel = this.knxChannel;

      //
      // Apply FuctionNames To KnxParameters
      //

      if (knxChannel) {
        knxChannel.applyFuctionNamesToKnxParameters();

        const knxInstanceData = (this.parent as Device)?.knxInstanceData;
        if (knxInstanceData) {
          knxChannel.applySceneConnectionsToKnxParameters(knxInstanceData);
        }
      }

      //
      // todo MWE: maybe we find a way in the future to do this a bit more async in the background
      //
      if (device.reEvalKnxMetadataNeeded) {
        device?.updateAllFromKnxMetadata();
      }

      const parent = this.parent as Device;
      if (parent) {
        parent.onChannelConnect(this, connection);
      }
    }
  }

  connectConsumer(page: Page, consumer: Consumer, askToSyncChannelType: boolean = true): Connection {
    this.parent?.raiseOnChange("@connectConsumer", "", true);
    if (this.parent?.className == "Actor") {
      this.parent?.parent?.raiseOnChange("@connectConsumer", "", true);
    }
    return this.connectObject(page, consumer, ConnectionType.Channel, askToSyncChannelType);
  }

  hasConnectionOnPage(page: Page, selectedOnly: boolean) {
    const consumer = this.getConnectedConsumer();
    if (selectedOnly && !consumer?.selected) {
      return false;
    }

    if (page.isFloorPlan) {
      const consumerParentRoom = consumer?.parent as Room;
      if (!consumerParentRoom?.isVisibleInFloorPlan) {
        return false;
      }
      const floor = page.parent as Floor;

      return floor == consumer?.parent?.parent;
    } else {
      return consumer?.parent == page.parent;
    }
  }

  getAllSignalsOfAllSubChannels(): Array<Signal> {
    let res = new Array<Signal>();

    this.signals.forEach((signal) => res.push(signal));

    this.subChannels?.forEach((subChannel) => {
      res = res.concat(subChannel.getAllSignalsOfAllSubChannels());
    });

    return res;
  }

  //
  // returns number of removed groupAdresses
  //
  removeAllGroupAdresses(): number {
    let res = 0;

    this.assetIsRequestChannel();
    this.signals.forEach((signal) => {
      res += signal.groupAddresses.length;
      signal.groupAddresses.clear();
    });

    return res;
  }

  channelTypesFromMapChannelTypeScript(): Array<{ channelType: ChannelType; text: string }> {
    const device = this.parent as Device;

    const supportedChannelTypes = device.mapChannelTypeConfig?.supportedChannelTypes;
    if (supportedChannelTypes) {
      return supportedChannelTypes.map((item) => ({
        channelType: item,
        text: itx("channelType." + item, item),
      }));
    }

    return [];
  }

  get allowedChannelTypes(): Array<{ channelType: ChannelType; text?: string }> | undefined {
    let knxChannel: Channel | undefined = this;

    const device = this.parentDevice;
    if (device?.template?.genericParams) {
      if (device.template.genericProductType === "Sensor" && this.tag === "T01") {
        return allowedChannelTypesForSensor;
      }
      if (device.template.genericProductType === "Button" && /^T(?:\d+(?:L|R)?(?:_l|_xl)?|P(?:_l)?)$/.test(this.tag)) {
        // Resolve the current configuration so splitting/merging never leaves a stale type list.
        return this.rockerConfig === RockerConfig.rocker ? allowedChannelTypesForRocker : allowedChannelTypesForSingle;
      }
    }
    if (device?.template?.knxProductData.hasScript_mapChannelType) {
      return this.channelTypesFromMapChannelTypeScript();
    }

    if (this.isRequestChannel) {
      knxChannel = this.knxChannel;
    }
    if (knxChannel?.knxChannelTypeProperty) {
      if ((this.parent as Device)?.template?.knxProductData.hasScript_mapChannelType) {
        return this.channelTypesFromMapChannelTypeScript();
      }

      const res = new Array<{ channelType: ChannelType; text: string }>();
      knxChannel.knxChannelTypeProperty.mapChannelTypeToValue.forEach((value, key) => {
        res.push({ channelType: key, text: value.optionDescription });
      });
      return res;
    } else {
      return [{ channelType: this.channelType }];
    }
  }

  get hasKnxChannelTypeProperty(): boolean {
    let knxChannel: Channel | undefined = this;
    if (this.isRequestChannel) {
      knxChannel = this.knxChannel;
    }
    if (knxChannel?.knxChannelTypeProperty) {
      return true;
    }
    return false;
  }

  get canControlChannelType(): boolean {
    const template = this.parentDevice?.template;
    if (
      template?.genericParams &&
      (template.genericProductType === "Button" || template.genericProductType === "Sensor")
    ) {
      return (this.allowedChannelTypes?.length ?? 0) > 1;
    }
    if (this.hasKnxChannelTypeProperty) {
      return true;
    }
    if ((this.parent as Device)?.template?.knxProductData.hasScript_setChannelType) {
      return true;
    }

    if ((this.parent as Device)?.template?.knxProductData.hasScript_mapChannelType) {
      return true;
    }
    return false;
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(this);
    this.signals.consistencyCheck(this);
    const isButtonChannel = this.isButtonChannel;

    if (isButtonChannel && !this.isLogicInputChannel && !this.isActorChannel && !this.isStatusChannel) {
      this.signals.forEach((signal) => {
        if (signal.groupAddresses.length > 1) {
          if (signal.type.isStatus) {
            this.logConsistencyError(`Action-Signal has more than one groupAddress`); // not itx
          }
        }
      });
    }

    if ((this.tag == TAG_ENVIRONMENT) != (this.channelType == ChannelType.Environment)) {
      this.logConsistencyError(
        `Channel: tag and channelType mismatch: tag=${this.tag}, channelType=${this.channelType} `,
      ); // not itx
      this.tag = TAG_ENVIRONMENT;
      this.channelType = ChannelType.Environment;
    }

    if (isButtonChannel) {
      if ((this.parent as BaseModel).isActor) {
        this.logConsistencyError(`ButtonChannel is Child of Actor`); // not itx
      }
    }

    // ButtonChannels of decentral actors can also be real ActorChannels (isActorChannel == true),
    // so the connection-type check below must apply to them too, not just plain Channel instances.
    if (!isButtonChannel || this.isActorChannel) {
      if (this.connections.size > 1) {
        this.logConsistencyError(`ActorChannel has more than one connection`); // not itx
      }

      if (this.connections.size == 1) {
        const connection = this.connections.asArray()[0];
        if (connection.type != ConnectionType.Channel) {
          this.logConsistencyError(`ActorChannel has a connection to another channel`);
          //
          // Fix the error
          //
          connection.type = ConnectionType.Channel;
        }
      }
    }
  }

  loadFromEstadaStorage = async (
    data: any,
    parent: BaseModel | undefined,
    loadData: (id: string | undefined, parentId: string | undefined) => any,
  ): Promise<boolean> => {
    //
    // make sure fromJSON can be called for this two properties,
    //  if they exist in the persistent data
    //

    if (data.knxChannelDescriptionProperty) {
      this.knxChannelDescriptionProperty = new ChannelDescriptionProperty();
    }
    if (data.knxChannelTypeProperty) {
      this.knxChannelTypeProperty = new ChannelTypeProperty();
    }

    const res = await super.loadFromEstadaStorage(data, parent, loadData);
    return res;
  };

  moveToNewParent(newParent: BaseModel): boolean {
    let moved = false;
    if (newParent.className == this.parent?.className) {
      // moce connection to this
      const newParentDevice = newParent as Device;
      const oldParent = this.parent as Device;
      this.connections.forEach((con) => {
        const partner = con.getPartner(this);

        if (partner) {
          const connectableChannel = newParentDevice.findConnectableChannel(con.type, partner);
          if (connectableChannel) {
            moved = this.moveConnection(connectableChannel) || moved;
          }
        }
        if (moved) {
          if ((newParent as Device)?.template) {
            (newParent as Device).updateAllFromKnxMetadataImmediately();
          }
          if (oldParent?.template) {
            oldParent.updateAllFromKnxMetadataImmediately();
          }
        }
      });
    }
    return moved;
  }

  moveConnection(dropChannel: Channel): boolean {
    function getAllNewAndOldChannels(
      fromChannel: Channel,
      toChannel: Channel,
    ): Array<{
      oldConnection: Connection;
      newConnection: Connection;
    }> {
      const dropDragChannelConnections = new Array<{
        oldConnection: Connection;
        newConnection: Connection;
      }>();

      fromChannel.connections.forEach((connection) => {
        const newConnection = new Connection({}, connection.parent);
        (connection.parent as Page).connections.add(newConnection);
        newConnection.type = connection.type;

        if (fromChannel?.id == connection.fromId && fromChannel) {
          newConnection.fromDevice = toChannel;
          newConnection.toDevice = connection.toDevice;
        } else if (fromChannel.id == connection.toId && fromChannel) {
          newConnection.fromDevice = connection.fromDevice;
          newConnection.toDevice = toChannel;
        } else {
          logger.error("connection wrong in Channel", fromChannel, connection);
        }
        dropDragChannelConnections.push({
          oldConnection: connection,
          newConnection: newConnection,
        });
      });

      return dropDragChannelConnections;
    }

    const dropDragChannelConnections = getAllNewAndOldChannels(this, dropChannel);

    dropDragChannelConnections.forEach((connection) => {
      connection.newConnection.addToConnectableObjects();
      connection.oldConnection.delete();
    });

    return dropDragChannelConnections.length > 0;
  }

  get isVirtualRocker(): boolean {
    return false;
  }

  set isVirtualRocker(newValue: boolean) {
    this.beforeChange("isVirtualRocker");
    logger.error("set isVirtualRocker is only implement for ButtonChannel");

    this.changed("isVirtualRocker");
  }

  set buttonSide(newValue: ButtonSide) {
    this.beforeChange("buttonSide");
    logger.error("set buttonSide is only implement for ButtonChannel");

    this.changed("buttonSide");
  }

  get buttonSide(): ButtonSide {
    return ButtonSide.both;
  }

  get rockerConfig(): RockerConfig {
    return RockerConfig.none;
  }

  set rockerConfig(newValue: RockerConfig) {
    logger.error("set rockerConfig is only implement for ButtonChannel");
  }

  getChannelNameForUI(): string {
    const template = this.parentDevice?.template;
    if (template?.genericParams && template.genericProductType === "Sensor" && this.tag === "T01") {
      return itx("genericDevice.sensorAction", "Sensor Aktion");
    }
    const knxChannel = this.knxChannel;
    let channelName = knxChannel?.name || "-/-";

    if (!knxChannel) {
      switch (this.tag) {
        case "TP":
          channelName = itx("channel.patchButton", "Patch");
          break;

        default:
          if (this.tag.startsWith("@I")) {
            channelName = "Import";
          } else if (this.isButtonChannel) {
            if (this.tag.startsWith("T")) {
              channelName = itx("components.Button", "Taste") + " " + this.tag.substring(1);
            } else if (this.tag.startsWith("S")) {
              channelName = itx("components.StatusLed", "Status") + " " + this.tag.substring(1);
            } else {
              channelName = itx("components.Channel", "Kanal") + " " + this.tag;
            }
            break;
          } else {
            channelName = itx("components.Channel", "Kanal") + " " + this.tag;
          }
      }
    }

    return channelName;
  }

  //
  // Sammle alle Gruppenadressen des Kanals
  //
  allGroupAddressesOfChannel(): Array<GroupAddress> {
    const res = new Set<GroupAddress>();
    this.signals.forEach((signal) => {
      signal.groupAddresses.asArray().forEach((ga) => {
        res.add(ga);
      });
    });
    return [...res];
  }

  //
  // Sammle alle über die Gruppenadressen verbundenen Actor-Kanäle
  //
  allConnectedActorChannelsViaGroupAddresses(groupAddressFilter?: (ga: GroupAddress) => boolean): Array<Channel> {
    const res = new Set<Channel>();

    for (const signal of this.signals.asArray()) {
      for (const c of signal.connectedActorChannelsViaGroupaddresses(groupAddressFilter)) {
        if (c != this) {
          res.add(c);
        }
      }
    }

    return [...res];
  }

  //
  // Sammle alle über die Gruppenadressen verbundenen Actor-Kanäle
  //
  allConnectedConsumersViaGroupAddresses(groupAddressFilter?: (ga: GroupAddress) => boolean): Array<Consumer> {
    const res = new Set<Consumer>();

    for (const signal of this.signals.asArray()) {
      for (const c of signal.connectedConsumersViaGroupaddresses(groupAddressFilter)) {
        res.add(c);
      }
    }

    return [...res];
  }

  get supportsRockerConfig(): boolean {
    return false;
  }

  get hasConnectedSignals(): boolean {
    for (const signal of this.signals) {
      if (signal.groupAddresses.length > 0) {
        return true;
      }
    }
    return false;
  }

  getName(): string | undefined {
    if (this._name) {
      return this._name;
    }

    switch (this.channelType) {
      case ChannelType.ManualSignals:
        return itx("Channel.ManualSignals", "Manuelle Signalverbindungen");

      case ChannelType.Environment:
        return itx("Channel.Environment", "Umgebung");

      case ChannelType.TemperatureMeasurement:
        return itx("Channel.TemperatureMeasurement", "Temperatur-Messung");

      case ChannelType.TemperatureSetpoint:
        return itx("Channel.TemperatureSetpoint", "Temperatur-Einstellung");

      case ChannelType.Heating:
        return itx("Channel.Heating", "Temperatur / Heizung");

      case ChannelType.Special:
        return itx("Channel.Special", "Spezial Signale");
    }

    return super.getName();
  }

  addSignalWithoutUpdate(signal: Signal) {
    const existingSignal = this.signals.findFirst((s) => s.number == signal.number);
    if (existingSignal) {
      productionAssert(false, _FILENAME_, 2470);
    } else {
      const oldSignals = (signal.parent as any)?.signals;
      if (oldSignals) {
        oldSignals.removeWithoutUpdate(signal);
      }
      signal.parent = this;
      this.signals.addWithoutUpdate(signal);
    }
  }
}

const falseValues = ["deactiv", "no", "nein", "false", "falsch", "off", "aus", "not"]; // no itx

function analyseBooleanValue(restriction: TypeRestriction): [string, string] {
  let trueEnumValue = "1";
  let falseEnumValue = "0";

  for (const enumeration of restriction.items) {
    if (matchN(falseValues, enumeration.a.Text)) {
      falseEnumValue = enumeration.a.Value;
    } else {
      trueEnumValue = enumeration.a.Value;
    }
  }

  return [trueEnumValue, falseEnumValue];
}

function getBooleanEnumValues(knxProductData: KnxProductData, item: any): [string, string] {
  if (knxProductData && item) {
    const typeRestriction = knxProductData.getParameterEnumeration(item);
    if (typeRestriction && typeRestriction.items.length == 2) {
      return analyseBooleanValue(typeRestriction);
    }
  }

  return ["1", "0"];
}

export function filterChannelsByUserRole(device: Device, channels: Array<Channel>): Array<Channel> {
  ///////////////////////////////////////////////////////////////////////////////////////////////
  //
  // Filter Channel for User-Roles

  //

  //
  // für generische Geräte (ConceptOne) werden alle Kanäle angezeigt
  //
  if (device.template?.genericParams) {
    return channels;
  }

  channels = channels.filter((knxChannel) => {
    if (knxChannel.channelType == ChannelType.Ignored && !isUiModeExpert()) {
      return false;
    }

    if (knxChannel.channelType == ChannelType.Environment && !isUiModeExpert()) {
      return false;
    }

    if (knxChannel.channelType == ChannelType.Special && !isUiModeExpert()) {
      return false;
    }
    if (knxChannel.tag == TAG_SPECIAL && !isUiModeExpert()) {
      return false;
    }

    // handle channel without knx channel and parent without template and connection
    if (knxChannel.connections.size != 0 || !knxChannel.parentDevice?.template) {
      return true;
    }

    // ignore channels without signals
    if (!isUiModeProductAdmin() && knxChannel.signals.size == 0) {
      return false;
    }

    // ignore channels without signals
    if (
      !isUiModeProductAdmin() &&
      (knxChannel.channelType == ChannelType.Unknown || knxChannel.channelType == ChannelType.General)
    ) {
      return false;
    }

    return true;
  });

  /////////////////////////////////////////////////////////////////////////////////////////////////

  return channels;
}

export function tagForChannelType(channelType: ChannelType): string | undefined {
  switch (channelType) {
    case ChannelType.ManualSignals:
      return TAG_MANUAL_SIGNALS;

    case ChannelType.Environment:
      return TAG_ENVIRONMENT;

    case ChannelType.TemperatureMeasurement:
    case ChannelType.TemperatureSetpoint:
    case ChannelType.TemperatureMeasurement:
      return TAG_TEMPERATUR;

    case ChannelType.Special:
      return TAG_SPECIAL;

    case ChannelType.Environment:
      return TAG_ENVIRONMENT;

    default:
      return undefined;
  }
}
